import {
  collection,
  doc,
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  orderBy,
  limit,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../firebase/firebase';
import { Task, TaskStatistics } from '../types/todoTypes';

const TASKS_COLLECTION = 'tasks';

/**
 * Create a new task
 */
export const createTask = async (
  taskData: Omit<Task, 'id' | 'createdAt' | 'lastEditedAt' | 'createdBy'>,
  currentUserId: string
): Promise<string> => {
  try {
    const newTask = {
      ...taskData,
      createdBy: currentUserId,
      createdAt: Timestamp.now(),
      lastEditedAt: null,
    };

    const docRef = await addDoc(collection(db, TASKS_COLLECTION), newTask);
    return docRef.id;
  } catch (error) {
    console.error('Error creating task:', error);
    throw new Error('Failed to create task');
  }
};

/**
 * Get tasks assigned to a specific user
 */
export const getTasksByUser = async (
  userId: string,
  pageSize: number = 10
): Promise<Task[]> => {
  try {
    const tasksQuery = query(
      collection(db, TASKS_COLLECTION),
      where('assignedTo', '==', userId),
      orderBy('createdAt', 'desc'),
      limit(pageSize)
    );

    const querySnapshot = await getDocs(tasksQuery);
    const tasks: Task[] = [];

    querySnapshot.forEach((doc) => {
      tasks.push({
        id: doc.id,
        ...doc.data(),
      } as Task);
    });

    return tasks;
  } catch (error) {
    console.error('Error fetching tasks by user:', error);
    throw new Error('Failed to fetch tasks');
  }
};

/**
 * Get task statistics
 */
export const getTaskStatistics = async (): Promise<TaskStatistics> => {
  try {
    // Get all tasks
    const allTasksQuery = query(collection(db, TASKS_COLLECTION));
    const allTasksSnapshot = await getDocs(allTasksQuery);

    const total = allTasksSnapshot.size;
    let pending = 0;
    let completed = 0;
    let createdToday = 0;

    const todayStart = new Date();
    todayStart.setHours(0, 0, 0, 0);

    allTasksSnapshot.forEach((doc) => {
      const task = doc.data() as Task;

      // Count by status
      if (task.status === 'Pending') {
        pending++;
      } else if (task.status === 'Complete') {
        completed++;
      }

      // Count created today
      if (task.createdAt && task.createdAt.toDate() >= todayStart) {
        createdToday++;
      }
    });

    return {
      total,
      pending,
      completed,
      createdToday,
    };
  } catch (error) {
    console.error('Error fetching task statistics:', error);
    return {
      total: 0,
      pending: 0,
      completed: 0,
      createdToday: 0,
    };
  }
};

/**
 * Update an existing task
 */
export const updateTask = async (
  taskId: string,
  updates: Partial<Omit<Task, 'id' | 'createdAt' | 'createdBy'>>
): Promise<void> => {
  try {
    const taskRef = doc(db, TASKS_COLLECTION, taskId);

    await updateDoc(taskRef, {
      ...updates,
      lastEditedAt: Timestamp.now(),
    });
  } catch (error) {
    console.error('Error updating task:', error);
    throw new Error('Failed to update task');
  }
};

/**
 * Delete a task
 */
export const deleteTask = async (taskId: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, TASKS_COLLECTION, taskId));
  } catch (error) {
    console.error('Error deleting task:', error);
    throw new Error('Failed to delete task');
  }
};

/**
 * Get a single task by ID
 */
export const getTaskById = async (taskId: string): Promise<Task | null> => {
  try {
    const taskDoc = await getDoc(doc(db, TASKS_COLLECTION, taskId));

    if (taskDoc.exists()) {
      return {
        id: taskDoc.id,
        ...taskDoc.data(),
      } as Task;
    }

    return null;
  } catch (error) {
    console.error('Error fetching task by ID:', error);
    throw new Error('Failed to fetch task');
  }
};

/**
 * Real-time listener for tasks assigned to a specific user
 */
export const listenToTasksByUser = (
  userId: string,
  pageSize: number = 10,
  callback: (tasks: Task[]) => void
): (() => void) => {
  const tasksQuery = query(
    collection(db, TASKS_COLLECTION),
    where('assignedTo', '==', userId),
    orderBy('createdAt', 'desc'),
    limit(pageSize)
  );

  const unsubscribe = onSnapshot(
    tasksQuery,
    (snapshot) => {
      const tasks: Task[] = [];
      snapshot.forEach((doc) => {
        tasks.push({
          id: doc.id,
          ...doc.data(),
        } as Task);
      });
      callback(tasks);
    },
    (error) => {
      console.error('Error listening to tasks:', error);
    }
  );

  return unsubscribe;
};

/**
 * Real-time listener for all tasks
 */
export const listenToAllTasks = (
  callback: (tasks: Task[]) => void
): (() => void) => {
  const tasksQuery = query(
    collection(db, TASKS_COLLECTION),
    orderBy('createdAt', 'desc')
  );

  const unsubscribe = onSnapshot(
    tasksQuery,
    (snapshot) => {
      const tasks: Task[] = [];
      snapshot.forEach((doc) => {
        tasks.push({
          id: doc.id,
          ...doc.data(),
        } as Task);
      });
      callback(tasks);
    },
    (error) => {
      console.error('Error listening to all tasks:', error);
    }
  );

  return unsubscribe;
};
